import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContextService } from './context.service';
import { Octopus, WidgetLayout } from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';

@Injectable()
export class WidgetLayoutService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  private readWithCriteria(
    criteria: Octopus.Criteria<Octopus.WidgetLayout>,
  ): Observable<WidgetLayout | null> {
    const request: Octopus.WidgetLayoutReadRequest = {
      WidgetLayout: {
        Criteria: criteria,
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            EntityType: 1,
            CreationDate: 1,
            Widgets: 1,
          },
        },
      },
    };

    return this.http
      .post<Octopus.WidgetLayoutResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.WidgetLayout?.Data?.length
          ) {
            const widgetLayout = result.WidgetLayout.Data[0];
            const mapper = new Octopus.WidgetLayoutMapper();
            return mapper.from(widgetLayout);
          }
          return null;
        }),
      );
  }

  readByEntityType(
    entityType: Octopus.EntityType,
  ): Observable<WidgetLayout | null> {
    return this.readWithCriteria({
      SearchQuery: {
        EntityType: { $EQ: entityType },
      },
    });
  }

  readByKey(key: string): Observable<WidgetLayout | null> {
    return this.readWithCriteria({
      SearchQuery: {
        Key: { $EQ: key },
      },
    });
  }

  readByTag(tag: string): Observable<WidgetLayout | null> {
    return this.readWithCriteria({
      SearchQuery: {
        Tag: { $EQ: tag },
      },
    });
  }

  update(
    key: string | null,
    entityType: Octopus.EntityType,
    widgetLayout: WidgetLayout,
    widgetData: string,
  ): Observable<string | null> {
    const request: Octopus.WidgetLayoutWriteRequest = {
      WidgetLayout: {
        Data: {
          Key: key,
          Name: widgetLayout.name,
          EntityType: entityType,
          Widgets: widgetData,
        },
      },
    };

    return this.http
      .post<Octopus.WidgetLayoutWriteResponse>(
        `${this.context.api}${key ? Octopus.Action.UPDATE : Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            return response.WidgetLayout.Data.Key;
          }
          return null;
        }),
      );
  }
}
